import React, { useState, useEffect, useRef, useCallback } from 'react';

interface CoachingTip {
  type: 'pace' | 'form' | 'breathing' | 'motivation';
  message: string;
}

interface RunGuidanceOverlayProps {
  phaseDuration: number;       // Current phase duration in seconds
  phaseElapsed: number;        // Seconds elapsed in current phase
  coachingTips: CoachingTip[]; // From phase data
  isVisible: boolean;
  isIntervalRepeat?: boolean;  // True if this is rep 2+ of an expanded interval — suppress tips
}

export const RunGuidanceOverlay: React.FC<RunGuidanceOverlayProps> = ({
  phaseDuration,
  phaseElapsed,
  coachingTips,
  isVisible,
  isIntervalRepeat = false
}) => {
  const [currentTip, setCurrentTip] = useState<CoachingTip | null>(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const shownIndicesRef = useRef<Set<number>>(new Set());
  const dismissTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fadeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Build a stable identity key from the tips content so we detect actual
  // phase changes rather than relying on array reference equality (which
  // breaks when React creates a new [] on every render via `|| []`).
  const tipsKey = coachingTips.length > 0
    ? coachingTips.map(t => `${t.type}:${t.message}`).join('|')
    : '';
  const prevTipsKeyRef = useRef(tipsKey);

  // Clean up all pending timers
  const clearTimers = useCallback(() => {
    if (dismissTimerRef.current) { clearTimeout(dismissTimerRef.current); dismissTimerRef.current = null; }
    if (fadeTimerRef.current) { clearTimeout(fadeTimerRef.current); fadeTimerRef.current = null; }
  }, []);

  // Reset tracking when phase actually changes (content-based, not reference-based)
  useEffect(() => {
    if (tipsKey !== prevTipsKeyRef.current) {
      prevTipsKeyRef.current = tipsKey;
      shownIndicesRef.current = new Set();
      clearTimers();
      setCurrentTip(null);
      setIsAnimating(false);
    }
  }, [tipsKey, clearTimers]);

  // Clean up on unmount
  useEffect(() => clearTimers, [clearTimers]);

  // Show tips at evenly spaced intervals across the phase
  useEffect(() => {
    if (!isVisible || coachingTips.length === 0 || phaseDuration <= 0 || isIntervalRepeat) return;

    const tipCount = coachingTips.length;
    // Space tips evenly: e.g., 4 tips in 600s → show at 75s, 225s, 375s, 525s
    // Start 1/(2N) into the phase, end 1/(2N) before the end
    const spacing = phaseDuration / tipCount;
    const offset = spacing / 2;

    for (let i = 0; i < tipCount; i++) {
      const triggerTime = Math.floor(offset + i * spacing);
      // Show if we're within a 5-second window of the trigger time
      if (
        phaseElapsed >= triggerTime &&
        phaseElapsed < triggerTime + 5 &&
        !shownIndicesRef.current.has(i)
      ) {
        shownIndicesRef.current.add(i);

        // Clear any pending dismiss from a previous tip
        clearTimers();

        setCurrentTip(coachingTips[i]);
        setIsAnimating(true);

        // Auto-dismiss: fade out after 6s, remove after fade completes
        dismissTimerRef.current = setTimeout(() => {
          setIsAnimating(false);
          fadeTimerRef.current = setTimeout(() => setCurrentTip(null), 300);
        }, 6000);

        break; // Only show one tip per render cycle
      }
    }
  }, [phaseElapsed, coachingTips, phaseDuration, isVisible, clearTimers]);

  if (!isVisible || !currentTip) return null;

  return (
    <div className="absolute top-20 left-4 right-4 z-10">
      <div className={`
        bg-gray-900/95 backdrop-blur-sm rounded-lg p-4 border-l-4
        ${getGuidanceColor(currentTip.type)}
        shadow-lg transition-all duration-300 transform
        ${isAnimating ? 'translate-y-0 opacity-100' : '-translate-y-4 opacity-0'}
      `}>
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-green-600 flex items-center justify-center">
            {getGuidanceIcon(currentTip.type)}
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-green-400 font-semibold text-sm">
                {getGuidanceTitle(currentTip.type)}
              </span>
            </div>
            <p className="text-white text-sm leading-relaxed">
              {currentTip.message}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// 🎨 UI HELPER FUNCTIONS
const GUIDANCE_COLORS: Record<string, string> = {
  'pace': 'border-blue-500',
  'form': 'border-green-500',
  'motivation': 'border-yellow-500',
  'breathing': 'border-purple-500',
};
const getGuidanceColor = (type: string): string => GUIDANCE_COLORS[type] ?? 'border-green-500';

const GUIDANCE_ICONS: Record<string, string> = {
  'pace': '⚡',
  'form': '🎯',
  'motivation': '💪',
  'breathing': '🫁',
};
const getGuidanceIcon = (type: string): string => GUIDANCE_ICONS[type] ?? '🌲';

const GUIDANCE_TITLES: Record<string, string> = {
  'pace': 'Pace Check',
  'form': 'Form Focus',
  'motivation': 'Keep Going',
  'breathing': 'Breathing',
};
const getGuidanceTitle = (type: string): string => GUIDANCE_TITLES[type] ?? 'Coaching';
